import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Play, Plus, Trash2, Mail, Instagram, RotateCcw, Zap } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(165, 145, 239, 0.5);
  animation: slideUp 0.3s ease;
  border: 1px solid ${props => props.theme.border};

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid ${props => props.theme.border};
  position: sticky;
  top: 0;
  background: ${props => props.theme.surface};
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
    color: ${props => props.theme.text};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DevicesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: ${props => props.theme.background};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
`;

const DeviceChip = styled.div`
  padding: 6px 12px;
  background: ${props => props.theme.primary}20;
  border: 1px solid ${props => props.theme.primary};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const ScriptsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ScriptItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${props => props.theme.background};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
`;

const ScriptOrder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.theme.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

const ScriptIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color}20;
  border: 1px solid ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ScriptInfo = styled.div`
  flex: 1;
`;

const ScriptName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
`;

const ScriptDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
    color: var(--error);
  }
`;

const AddScriptButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${props => props.theme.border};
  border: 2px dashed ${props => props.theme.textSecondary};
  border-radius: 8px;
  color: ${props => props.theme.text};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary}20;
    border-color: ${props => props.theme.primary};
    color: ${props => props.theme.primary};
  }
`;

const ScriptSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const ScriptOption = styled.button`
  padding: 16px;
  background: ${props => props.theme.background};
  border: 2px solid ${props => props.selected ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
  }
`;

const ScriptOptionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  border: 1px solid ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScriptOptionName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.text};
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid ${props => props.theme.border};
  position: sticky;
  bottom: 0;
  background: ${props => props.theme.surface};
`;

const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${props => props.theme.border};
  color: ${props => props.theme.text};

  &:hover:not(:disabled) {
    background: ${props => props.theme.textSecondary};
  }
`;

const LaunchButton = styled(Button)`
  background: ${props => props.theme.primary};
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.theme.primary}50;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const AVAILABLE_SCRIPTS = [
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Créer un compte Gmail',
    icon: Mail,
    color: 'var(--error)'
  },
  {
    id: 'interact',
    name: 'Interact',
    description: 'Interactions Instagram',
    icon: Instagram,
    color: '#E1306C'
  },
  {
    id: 'unfollow',
    name: 'Unfollow',
    description: 'Désabonnements',
    icon: RotateCcw,
    color: '#FFA500'
  },
  {
    id: 'restarter',
    name: 'Restarteur',
    description: 'Redémarrer connexion',
    icon: Zap,
    color: '#FFD700'
  }
];

const WorkflowModal = ({ devices, onClose, onLaunch, theme }) => {
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [showScriptSelector, setShowScriptSelector] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  const addScript = (scriptId) => {
    const script = AVAILABLE_SCRIPTS.find(s => s.id === scriptId);
    if (script) {
      setSelectedScripts([...selectedScripts, script]);
      setShowScriptSelector(false);
    }
  };

  const removeScript = (index) => {
    setSelectedScripts(selectedScripts.filter((_, i) => i !== index));
  };

  const handleLaunch = async () => {
    if (selectedScripts.length === 0) return;
    
    setIsLaunching(true);
    try {
      await onLaunch(devices.map(d => d.device_id), selectedScripts.map(s => s.id));
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            <Play size={24} color={theme.primary} />
            Créer un Workflow
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionTitle theme={theme}>Devices Sélectionnés ({devices.length})</SectionTitle>
            <DevicesList theme={theme}>
              {devices.map(device => (
                <DeviceChip key={device.device_id} theme={theme}>
                  {device.device_name || device.device_id}
                </DeviceChip>
              ))}
            </DevicesList>
          </Section>

          <Section>
            <SectionTitle theme={theme}>Scripts à Exécuter ({selectedScripts.length})</SectionTitle>
            <ScriptsList>
              {selectedScripts.map((script, index) => (
                <ScriptItem key={index} theme={theme}>
                  <ScriptOrder theme={theme}>{index + 1}</ScriptOrder>
                  <ScriptIcon color={script.color}>
                    <script.icon size={20} color={script.color} />
                  </ScriptIcon>
                  <ScriptInfo>
                    <ScriptName theme={theme}>{script.name}</ScriptName>
                    <ScriptDescription theme={theme}>{script.description}</ScriptDescription>
                  </ScriptInfo>
                  <RemoveButton theme={theme} onClick={() => removeScript(index)}>
                    <Trash2 size={18} />
                  </RemoveButton>
                </ScriptItem>
              ))}
              
              {!showScriptSelector && (
                <AddScriptButton theme={theme} onClick={() => setShowScriptSelector(true)}>
                  <Plus size={18} />
                  Ajouter un Script
                </AddScriptButton>
              )}

              {showScriptSelector && (
                <ScriptSelector>
                  {AVAILABLE_SCRIPTS.map(script => (
                    <ScriptOption
                      key={script.id}
                      theme={theme}
                      onClick={() => addScript(script.id)}
                    >
                      <ScriptOptionIcon color={script.color}>
                        <script.icon size={24} color={script.color} />
                      </ScriptOptionIcon>
                      <ScriptOptionName theme={theme}>{script.name}</ScriptOptionName>
                    </ScriptOption>
                  ))}
                </ScriptSelector>
              )}
            </ScriptsList>
          </Section>
        </ModalBody>

        <ModalFooter theme={theme}>
          <CancelButton theme={theme} onClick={onClose} disabled={isLaunching}>
            Annuler
          </CancelButton>
          <LaunchButton 
            theme={theme} 
            onClick={handleLaunch} 
            disabled={selectedScripts.length === 0 || isLaunching}
          >
            <Play size={18} />
            {isLaunching ? 'Lancement...' : `Lancer le Workflow`}
          </LaunchButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default WorkflowModal;
